import type { GlobalSettings, ContentfulApiInitConfig, ContentPageEntryType } from 'tsconfig/types.d';
import { ContentfulClient } from './contentful/contentfulClient';
import { compressContentfulJson, logger } from 'common-ui';

type ContentfulApi = {
  fetchListOfAllContentPages: () => Promise<any>;
  fetchListOfAllLandingPages: () => Promise<any>;
  fetchSiteConfig: () => Promise<any>;
  fetchContentPageForSlug: (slug: string) => Promise<ContentPageEntryType>;
  fetchContentPageForId: (id: string) => Promise<any>;
  fetchLandingPageForSlug: (slug: string) => Promise<any>;
};

// Define `initContentfulAPI` before `initContentfulAPIWithGlobalSettings`
export const initContentfulAPI = (config: ContentfulApiInitConfig): ContentfulApi => {
  const contentfulClient = new ContentfulClient(config);
  const client = contentfulClient.primaryClient;

  const processResponse = (response: any) => {
    if (config.isPreview) {
      return response;
    }
    return compressContentfulJson(response);
  };

  return {
    // Fetches a list of all Content Pages with pagination
    // Note: Pages are already filtered by site via Contentful space/environment
    fetchListOfAllContentPages: async () => {
      try {
        const limit = 1000; // Max limit per request

        const getPageNextToken = (nextPageValue?: string): string | undefined => {
          if (!nextPageValue) {
            return undefined;
          }

          if (!nextPageValue.includes('://')) {
            return nextPageValue;
          }

          try {
            return new URL(nextPageValue).searchParams.get('pageNext') || undefined;
          } catch (error) {
            logger.error('fetchListOfAllContentPages: Could not parse Contentful pages.next URL', error);
            return undefined;
          }
        };

        // Recursive function to fetch all pages with offset pagination
        // This is used only as a fallback when cursor pagination is unavailable.
        const fetchPaginatedPages = async (skip = 0, allItems: any[] = []): Promise<any[]> => {
          // Safety check to prevent infinite loops
          if (skip >= 10000) {
            logger.error('fetchListOfAllContentPages: Too many pages, stopping pagination at 10000');
            return allItems;
          }

          const queryParams: any = {
            content_type: 'contentPage',
            include: 1, // Include one level of linked entries so seoMeta.robots is available
            limit,
            skip,
          };

          const rawPagesData = await client.withoutUnresolvableLinks.getEntries(queryParams);
          const newItems = allItems.concat(rawPagesData.items);

          // Check if there are more items to fetch
          const hasMorePages = rawPagesData.total > skip + limit;

          if (hasMorePages) {
            return fetchPaginatedPages(skip + limit, newItems);
          }

          return newItems;
        };

        // Prefer cursor-based pagination. It avoids skip limits and can fetch beyond 10k items.
        let firstPage: any;
        try {
          firstPage = await client.withoutUnresolvableLinks.getEntries({
            content_type: 'contentPage',
            include: 1,
            limit,
            cursor: true,
          } as any);
        } catch (error) {
          logger.warn('fetchListOfAllContentPages: Cursor request failed, falling back to skip pagination');
          const fallbackItems = await fetchPaginatedPages(0, []);
          const fallbackRequestCount = Math.max(1, Math.ceil(fallbackItems.length / limit));
          logger.log(
            `Fetched ${fallbackItems.length} total pages from Contentful (across ${fallbackRequestCount} requests)`,
          );
          return processResponse(fallbackItems);
        }

        const firstItems = Array.isArray(firstPage?.items) ? firstPage.items : [];
        const firstPageToken = getPageNextToken(firstPage?.pages?.next);

        let allItems: any[];
        let requestCount = 1;

        if (firstPageToken !== undefined || firstPage?.pages !== undefined) {
          allItems = firstItems;
          const seenPageTokens = new Set<string>();

          const fetchCursorPages = async (pageToken: string, items: any[]): Promise<any[]> => {
            if (seenPageTokens.has(pageToken)) {
              logger.error('fetchListOfAllContentPages: Duplicate pageNext token detected, stopping pagination');
              return items;
            }

            seenPageTokens.add(pageToken);

            const pageResponse: any = await client.withoutUnresolvableLinks.getEntries({
              content_type: 'contentPage',
              include: 1,
              limit,
              pageNext: pageToken,
            } as any);

            requestCount += 1;
            const updatedItems = items.concat(pageResponse?.items || []);
            const nextPageToken = getPageNextToken(pageResponse?.pages?.next);

            if (!nextPageToken) {
              return updatedItems;
            }

            return fetchCursorPages(nextPageToken, updatedItems);
          };

          allItems = firstPageToken ? await fetchCursorPages(firstPageToken, allItems) : allItems;
        } else {
          logger.warn('fetchListOfAllContentPages: Cursor pagination unavailable, falling back to skip pagination');

          const total = Number(firstPage?.total) || firstItems.length;
          if (total <= limit) {
            allItems = firstItems;
          } else {
            allItems = await fetchPaginatedPages(limit, firstItems);
            requestCount = Math.ceil(allItems.length / limit);
          }
        }

        logger.log(`Fetched ${allItems.length} total pages from Contentful (across ${requestCount} requests)`);
        return processResponse(allItems);
      } catch (e) {
        logger.error('Error', e);
      }
    },

    //fetches a list of all Landing Pages
    fetchListOfAllLandingPages: async () => {
      try {
        const rawPagesData = await client.withoutUnresolvableLinks.getEntries({
          content_type: 'acquisitionLandingPage',
          //no child items - we only want the list of pages
          include: 0,
        });
        return processResponse(rawPagesData.items);
      } catch (e) {
        logger.error('Error', e);
      }
    },

    fetchSiteConfig: async () => {
      const siteConfig = await contentfulClient.fetchSiteConfig();
      return processResponse(siteConfig);
    },

    fetchContentPageForSlug: async (slug: string): Promise<ContentPageEntryType> => {
      const page = await contentfulClient.fetchContentPage({ 'fields.slug': slug });
      if (page && page?.length > 1) {
        logger.error(
          `More than 1 ContentPage item found for slug '${slug}'. Using first`,
          JSON.stringify(page, null, 2),
        );
      }
      return processResponse(page?.[0]);
    },

    // fetches a single Content Page by its ID
    fetchContentPageForId: async (id: string) => {
      const page = await contentfulClient.fetchContentPage({ 'fields.id': id });
      if (page && page?.length > 1) {
        logger.error(`More than 1 ContentPage item found for id '${id}'. Using first`, JSON.stringify(page, null, 2));
      }
      return page?.[0];
    },

    // Fetch Landing Page content by slug
    fetchLandingPageForSlug: async (slug: string) => {
      const page = await contentfulClient.fetchLandingPage({ 'fields.slug': slug });
      if (page && page?.length > 1) {
        logger.error(
          `More than 1 ContentPage item found for slug '${slug}'. Using first`,
          JSON.stringify(page, null, 2),
        );
      }
      return processResponse(page?.[0]);
    },
  };
};

// Use `initContentfulAPI` after its definition
export const initContentfulAPIWithGlobalSettings = (globalSettings: GlobalSettings): ContentfulApi =>
  initContentfulAPI({
    space: globalSettings.contentfulSpace as string,
    environment: globalSettings.contentfulEnvironment as string,
    accessToken: (globalSettings.contentfulPreviewMode
      ? globalSettings.contentfulPreviewToken
      : globalSettings.contentfulToken) as string,
    host: globalSettings.contentfulPreviewMode ? 'preview.contentful.com' : 'cdn.contentful.com',
    contentfulOrchestration: globalSettings.contentfulOrchestration as string,
    isPreview: globalSettings.contentfulPreviewMode as boolean,
  });
